import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, X, Check, ChevronDown, Beaker } from "lucide-react";
import bookLabIcon from "../../assets/booklabicon.svg";
import {
  getLabDisplayName,
  getLabDisplayPrice,
  getLabItemKey,
  getLabSectionKey,
} from "./labTestUtils";



const SECTION_ORDER = ["PATHOLOGY", "RADIOLOGY", "PACKAGES"];

function AddLabTestModal({
  isOpen,
  onClose,
  patientName = "",
  appointmentId = "",
  pincode = "751009",
  existingCartLabCodes = new Set(),
  existingOrderLabKeys = new Set(),
  onAddToOrder,
  isSubmitting = false,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [labFilter, setLabFilter] = useState("all");
  const [selectedTest, setSelectedTest] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setLabFilter("all");
      setSelectedTest(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const trimmed = searchQuery.trim();
    if (trimmed.length < 3) {
      setSearchResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_DIAGNOSISURL}partner-labs/searchByPinCode?labCategory=LAB&freeTextSearch=${encodeURIComponent(trimmed)}&pincode=${pincode}`,
        );
        const content = response?.data?.content;
        setSearchResults(Array.isArray(content) ? content : []);
      } catch {
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, isOpen, pincode]);

  const labOptions = useMemo(() => {
    const names = new Set();
    searchResults.forEach((item) => {
      if (item.diagnosticsName) names.add(item.diagnosticsName);
    });
    return ["all", ...Array.from(names).sort()];
  }, [searchResults]);

  const filteredResults = useMemo(() => {
    if (labFilter === "all") return searchResults;
    return searchResults.filter((item) => item.diagnosticsName === labFilter);
  }, [searchResults, labFilter]);

  const groupedResults = useMemo(() => {
    const groups = {};
    filteredResults.forEach((item) => {
      const key = getLabSectionKey(item);
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return SECTION_ORDER.filter((k) => groups[k]?.length).map((k) => ({
      label: k,
      items: groups[k],
    }));
  }, [filteredResults]);

  const totalPrice = selectedTest
    ? getLabDisplayPrice(selectedTest.item) || 0
    : 0;

  const isDisabledItem = (item) => {
    const key = getLabItemKey(item);
    const code = item.partnerTestCode || item.labTestCode;
    return (
      existingOrderLabKeys.has(key) ||
      (code && existingCartLabCodes.has(code))
    );
  };

  const selectTest = (item) => {
    if (isDisabledItem(item)) return;
    setSelectedTest((prev) => {
      if (prev?.item && getLabItemKey(prev.item) === getLabItemKey(item)) {
        return null;
      }
      return { item };
    });
  };

  const handleAddToOrder = () => {
    if (!selectedTest) return;
    onAddToOrder([selectedTest]);
  };

  if (!isOpen) return null;

  const hasSearch = searchQuery.trim().length >= 3;
  const resultCount = filteredResults.length;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-[#004172] to-[#3cba84] px-5 py-4 text-white flex items-start justify-between gap-4 flex-shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            <span className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <img src={bookLabIcon} alt="" className="w-6 h-6 object-contain" />
            </span>
            <div className="min-w-0">
              <h2 className="text-xl font-bold mb-0.5">Add Lab Test</h2>
              <p className="text-white/85 text-sm mb-0 truncate">
                Search and select tests for {patientName || "Patient"}
                {appointmentId ? ` · Appt #${appointmentId}` : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-lg border border-white/40 flex items-center justify-center hover:bg-white/10 flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pt-4 pb-3 border-b border-gray-100 flex-shrink-0 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search lab tests e.g. CBC, lipid profile..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0D6B6E]/30 focus:border-[#0D6B6E]"
              autoFocus
            />
          </div>

          {hasSearch && labOptions.length > 1 && (
            <div className="relative">
              <select
                value={labFilter}
                onChange={(e) => setLabFilter(e.target.value)}
                className="w-full appearance-none border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0D6B6E]/30"
              >
                <option value="all">All Labs</option>
                {labOptions
                  .filter((l) => l !== "all")
                  .map((lab) => (
                    <option key={lab} value={lab}>
                      {lab}
                    </option>
                  ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          )}

          {hasSearch && (
            <p className="text-xs text-gray-500 mb-0 mt-2">
              {loading
                ? "Searching..."
                : `${resultCount} result${resultCount === 1 ? "" : "s"} found for '${searchQuery.trim()}'`}
            </p>
          )}
          {!hasSearch && (
            <p className="text-xs text-gray-400 mb-0 mt-2">
              Type at least 3 characters to search lab tests
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {!hasSearch && (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center text-gray-400">
              <Beaker className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm mb-0">Start typing to find lab tests</p>
            </div>
          )}

          {hasSearch && !loading && resultCount === 0 && (
            <div className="py-12 text-center text-gray-400 text-sm">
              No lab tests found. Try a different search term.
            </div>
          )}

          {groupedResults.map((group) => (
            <div key={group.label}>
              <div className="px-5 py-2 bg-gray-50 border-y border-gray-100">
                <span className="text-[11px] font-bold tracking-wide text-gray-500">
                  {group.label}
                </span>
              </div>
              {group.items.map((item) => {
                const key = getLabItemKey(item);
                const disabled = isDisabledItem(item);
                const isSelected =
                  selectedTest?.item &&
                  getLabItemKey(selectedTest.item) === key;
                const displayPrice = getLabDisplayPrice(item);
                const testName = getLabDisplayName(item);
                const inCart =
                  item.partnerTestCode &&
                  existingCartLabCodes.has(item.partnerTestCode);

                return (
                  <div
                    key={key}
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    onClick={() => !disabled && selectTest(item)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        selectTest(item);
                      }
                    }}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-gray-50 transition-colors ${
                      isSelected
                        ? "bg-[#E8F6F4] border-l-4 border-l-[#0D6B6E]"
                        : "border-l-4 border-l-transparent hover:bg-gray-50/80"
                    } ${disabled ? "cursor-default opacity-80" : "cursor-pointer"}`}
                  >
                    <input
                      type="radio"
                      name="lab-test-select"
                      checked={isSelected}
                      disabled={disabled}
                      onChange={() => selectTest(item)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 border-gray-300 text-[#0D6B6E] focus:ring-[#0D6B6E] flex-shrink-0 disabled:opacity-40"
                    />

                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: "#F1F5F9" }}
                    >
                      <Beaker className="w-5 h-5 text-[#0D6B6E]" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#0D6B6E] mb-0 text-sm truncate">
                        {testName}
                      </p>
                      <p className="text-xs text-gray-500 mb-0 truncate">
                        {item.diagnosticsName || item.labType || ""}
                      </p>
                      {inCart && (
                        <span className="text-[10px] font-semibold text-green-700">
                          In cart
                        </span>
                      )}
                    </div>

                    {displayPrice != null && (
                      <span className="font-bold text-[#003A71] text-sm flex-shrink-0 whitespace-nowrap">
                        ₹{displayPrice.toLocaleString("en-IN")}
                      </span>
                    )}

                    {disabled ? (
                      <button
                        type="button"
                        disabled
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-500 flex-shrink-0"
                      >
                        Added
                      </button>
                    ) : isSelected ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectTest(item);
                        }}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-50 text-green-700 border border-green-200 flex-shrink-0"
                      >
                        <Check className="w-3.5 h-3.5" />
                        Added
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectTest(item);
                        }}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-[#0D6B6E]/30 text-[#0D6B6E] hover:bg-[#0D6B6E]/5 flex-shrink-0"
                      >
                        + Add
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {loading && hasSearch && (
            <div className="py-12 text-center text-gray-500 text-sm">
              Loading lab tests...
            </div>
          )}
        </div>

        {selectedTest && (
          <div className="px-5 py-2 border-t border-gray-100 flex flex-wrap gap-2 flex-shrink-0 bg-gray-50/50">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-gray-200 text-xs text-gray-700">
              {getLabDisplayName(selectedTest.item)}
              {selectedTest.item.diagnosticsName
                ? ` – ${selectedTest.item.diagnosticsName}`
                : ""}
              <button
                type="button"
                onClick={() => setSelectedTest(null)}
                className="text-gray-400 hover:text-gray-700"
                aria-label={`Remove ${getLabDisplayName(selectedTest.item)}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          </div>
        )}

        <div className="px-5 py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-shrink-0 bg-white">
          <p className="text-sm text-gray-600 mb-0">
            {selectedTest ? (
              <>
                <span className="font-semibold text-[#0D6B6E]">1 test selected</span>
                {totalPrice > 0 && (
                  <>
                    {" "}
                    · Total:{" "}
                    <span className="font-bold text-[#003A71]">
                      ₹{totalPrice.toLocaleString("en-IN")}
                    </span>
                  </>
                )}
              </>
            ) : (
              "Select a lab test to add to the order"
            )}
          </p>
          <div className="flex items-center gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddToOrder}
              disabled={!selectedTest || isSubmitting}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#004172] to-[#3cba84] text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              + Add to Order
              {selectedTest && (
                <span className="w-6 h-6 rounded-full bg-white/25 flex items-center justify-center text-xs font-bold">
                  1
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddLabTestModal;
